import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent } from '../../../shared/components/ui';
import { buildAvgFeeAmountTrendData } from '../../../shared/services/insightsApi';
import { formatCurrencyCompact } from '../../../shared/utils/formatters';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{label}</p>
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 bg-emerald-500" />
        <span className="text-gray-700 dark:text-gray-200">
          Avg Fee:{' '}
          <span className="font-medium">
            {payload[0].value != null
              ? `$${payload[0].value.toLocaleString()}`
              : '—'}
          </span>
        </span>
      </div>
      {d?.count != null && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          {d.count} estimate{d.count !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

function formatYAxis(value) {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value}`;
}

/**
 * AvgFeeAmountTrendChart — Area chart showing average fee dollar amount per month over time.
 * The fee category is identified by categoryCode === '00', using feeCat.subtotal.
 * Props: estimates (Array) — raw filtered estimates from Insights.jsx
 */
export function AvgFeeAmountTrendChart({ estimates }) {
  const data = useMemo(() => buildAvgFeeAmountTrendData(estimates), [estimates]);

  const overall =
    data.length > 0
      ? Math.round(data.reduce((s, d) => s + d.avgFeeAmount, 0) / data.length)
      : null;

  return (
    <Card className="shadow-sm">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Avg Fee per Month Over Time</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Monthly average of the total fee amount across all estimates
            </p>
          </div>
          {overall != null && (
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-900/40 px-2.5 py-1 rounded-full">
              Avg {formatCurrencyCompact(overall)}
            </span>
          )}
        </div>
      </div>

      <CardContent className="pt-4 pb-2">
        {data.length < 2 ? (
          <div className="flex flex-col items-center justify-center h-56 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No fee amount trend data available</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Estimates with a fee category (code 00) and subtotal will appear here
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="feeAmountGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tickFormatter={formatYAxis}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                width={60}
              />
              <Tooltip content={<CustomTooltip />} />
              {overall != null && (
                <ReferenceLine
                  y={overall}
                  stroke="#10b981"
                  strokeDasharray="4 3"
                  strokeOpacity={0.5}
                />
              )}
              <Area
                type="monotone"
                dataKey="avgFeeAmount"
                stroke="#10b981"
                strokeWidth={2.5}
                fill="url(#feeAmountGradient)"
                dot={{ r: 3.5, fill: '#10b981', strokeWidth: 0 }}
                activeDot={{ r: 5, fill: '#10b981' }}
                connectNulls
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default AvgFeeAmountTrendChart;
